import { createContext, Dispatch, PropsWithChildren, ReactNode, SetStateAction, useContext, useState } from 'react';

import { Role } from 'types';
import { FilterState, LogicalOperator } from 'utils';

export type BaseDataType = {
  id: number;
  createdBy?: string | null;
  createdDate?: Date | string | number | null;
  lastUpdatedBy?: string | null;
  lastUpdatedDate?: Date | string | number | null;
  [P: string]: any;
};

export enum SortDirection {
  ASCENDING = 'ASC',
  DESCENDING = 'DESC'
}

interface FetchArgs {
  filterString?: string;
  sortField?: string;
  sortDirection?: SortDirection;
}

export interface FetchCsvArgs extends FetchArgs {
  columns: string[];
  headers?: string[];
}

export interface FetchDataArgs extends FetchArgs {
  from?: Date;
  to?: Date;
  pageNum?: number;
  pageSize?: number;
  quickSearch?: string;
}

type Pagination = Pick<FetchDataArgs, 'pageNum' | 'pageSize' | 'sortDirection' | 'sortField'> & {
  totalCount: number;
  totalPages: number;
};

export interface ControlledDataTableColumn<
  T extends BaseDataType,
  U extends string & keyof T = any,
  V extends T[U] = any,
  W extends any = V
> {
  field: U;
  label: string;
  getValueLabel?: (val: V, row: T) => W;
  renderCell?: (val: V, row: T) => ReactNode;
  allowedRoles?: Role[];
  dateFormat?: string;
  filterable?: boolean;
  skipQuickSearch?: boolean;
  sortable?: boolean;
  sortField?: string;
  type?: string;
}

export interface ControlledDataTableContextType<T extends BaseDataType> {
  columnVisibility: Map<keyof T, boolean>;
  setColumnVisibility: Dispatch<Map<string, boolean>>;
  data: T[];
  setData: Dispatch<SetStateAction<T[]>>;
  rowSelection: Map<number, boolean>;
  setRowSelection: Dispatch<SetStateAction<Map<number, boolean>>>;
  loading: boolean;
  setLoading: Dispatch<boolean>;
  searchInput: string;
  setSearchInput: Dispatch<SetStateAction<string>>;
  filterState: FilterState;
  setFilterState: Dispatch<SetStateAction<FilterState>>;
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
}

const ControlledDataTableContext = createContext<ControlledDataTableContextType<any>>({
  columnVisibility: new Map(),
  setColumnVisibility: () => {},
  data: [],
  setData: () => {},
  loading: false,
  setLoading: () => {},
  filterState: { filters: [], logicalOperator: LogicalOperator.AND },
  setFilterState: () => {},
  pagination: { totalCount: 0, totalPages: 0 },
  setPagination: () => {},
  rowSelection: new Map(),
  setRowSelection: () => {},
  searchInput: '',
  setSearchInput: () => {}
});

export const useDataTableContext = <T extends BaseDataType>() =>
  useContext<ControlledDataTableContextType<T>>(ControlledDataTableContext);

export const ControlledDataTableProvider = <T extends BaseDataType>({
  children,
  initialColumns = [],
  initialPagination = {},
  ...contextOverrides
}: PropsWithChildren<Partial<ControlledDataTableContextType<T>>> & {
  initialColumns?: string[];
  initialPagination?: Partial<Pagination>;
}) => {
  const [data, setData] = useState<T[]>([]);

  const [loading, setLoading] = useState(true);

  const [columnVisibility, setColumnVisibility] = useState<Map<keyof T, boolean>>(
    initialColumns.reduce((acc, field) => acc.set(field, true), new Map())
  );
  const [filterState, setFilterState] = useState<FilterState>({
    filters: [],
    logicalOperator: LogicalOperator.AND
  });
  const [searchInput, setSearchInput] = useState<string>('');
  const [rowSelection, setRowSelection] = useState<Map<number, boolean>>(new Map());

  const [pagination, setPagination] = useState<Pagination>({
    pageNum: 0,
    pageSize: 100,
    sortDirection: SortDirection.DESCENDING,
    sortField: 'createdDate',
    ...initialPagination,
    totalCount: 0,
    totalPages: 0
  });

  return (
    <ControlledDataTableContext.Provider
      value={{
        columnVisibility,
        setColumnVisibility,
        data,
        setData,
        loading,
        setLoading,
        pagination,
        setPagination,
        filterState,
        setFilterState,
        searchInput,
        setSearchInput,
        rowSelection,
        setRowSelection,
        ...contextOverrides
      }}
    >
      {children}
    </ControlledDataTableContext.Provider>
  );
};
